import { useEffect, useState } from "react";
import styled from "styled-components";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  getDoc,
  setDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { getLocalDateKey } from "../utils/date";
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock3,
  Layers3,
  Sparkles,
  Target,
} from "lucide-react";

const STATUSES = ["TODO", "IN_PROGRESS", "DONE"];

/* ================= UTIL ================= */

const getDayStatusByIndex = (index, startedAt, completedDays = {}) => {
  if (!startedAt) return "upcoming";

  const start = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);
  const today = new Date();

  const startDate = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  );
  const todayDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const absoluteCurrentDay = Math.floor((todayDate - startDate) / 86400000) + 1;
  const viewedDay = index + 1;

  const key = getLocalDateKey(todayDate);

  if (completedDays[key]) return "done";
  if (viewedDay === absoluteCurrentDay) return "today";
  if (viewedDay < absoluteCurrentDay) return "missed";
  return "upcoming";
};

/* ================= COMPONENT ================= */
const getDayDateByIndex = (startedAt, index) => {
  const base = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);

  return new Date(base.getFullYear(), base.getMonth(), base.getDate() + index);
};
export default function TasksBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const roadmapId = searchParams.get("roadmapId");
  const dayId = searchParams.get("dayId");

  const [loading, setLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [hasProAccess, setHasProAccess] = useState(false);

  const [roadmap, setRoadmap] = useState(null);
  const [completedDays, setCompletedDays] = useState({});
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  const [tasks, setTasks] = useState([]);

  const [remainingTime, setRemainingTime] = useState(() =>
    getRemainingTimeToday()
  );
  useEffect(() => {
    setRemainingTime(getRemainingTimeToday());

    const interval = setInterval(() => {
      setRemainingTime(getRemainingTimeToday());
    }, 60000);

    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    if (!user || !roadmapId || !dayId) return;

    const load = async () => {
      setLoading(true);

      /* ---- roadmap ---- */
      const roadmapSnap = await getDoc(doc(db, "roadmaps", user.uid));
      if (!roadmapSnap.exists()) return setLoading(false);

      const roadmapData = roadmapSnap.data();
      setRoadmap(roadmapData);
      setCompletedDays(roadmapData.completedDays || {});

      const userSnap = await getDoc(doc(db, "users", user.uid));
      setHasProAccess(userSnap.exists() && userSnap.data()?.plan === "PRO");

      /* ---- day ---- */
      const daySnap = await getDoc(doc(db, "roadmap_days", dayId));
      if (!daySnap.exists()) return setLoading(false);

      const day = { id: daySnap.id, ...daySnap.data() };

      const dayIndex = day.absoluteDayIndex ?? 0;
      setCurrentDayIndex(dayIndex);

      const status = getDayStatusByIndex(
        dayIndex,
        roadmapData.startedAt,
        roadmapData.completedDays || {}
      );

      setIsLocked(status === "upcoming");

      /* ---- tasks ---- */
      const generated = (day.tasks || []).map((title, i) => ({
        id: `${dayId}_task_${i}`,
        title,
        status: "TODO",
        sequence: i + 1,
        roadmapId,
        dayId,
      }));

      const progressSnap = await getDoc(
        doc(db, "users", user.uid, "days", dayId)
      );

      const saved = progressSnap.exists()
        ? progressSnap.data().tasks || {}
        : {};

      setTasks(
        generated.map((t) => ({
          ...t,
          status: saved[t.id]?.status || "TODO",
        }))
      );

      setLoading(false);
    };

    load();
  }, [user, roadmapId, dayId]);

  const allTasksCompleted = tasks.every((t) => t.status === "DONE");
  const countdownVariant = getCountdownVariant(remainingTime);
  /* ================= LOAD ================= */

  function getRemainingTimeToday() {
    const now = new Date();

    const endOfDay = new Date();
    endOfDay.setHours(23, 59, 59, 999);

    const diffMs = endOfDay.getTime() - now.getTime();

    if (diffMs <= 0) return { hours: 0, minutes: 0 };

    const totalMinutes = Math.floor(diffMs / 60000);
    return {
      hours: Math.floor(totalMinutes / 60),
      minutes: totalMinutes % 60,
    };
  }

  const formatRemainingToMidnight = ({ hours, minutes }) => {
    if (hours <= 0 && minutes <= 0) return "Day over";
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m left`;
    if (hours > 0) return `${hours}h left`;
    return `${minutes}m left`;
  };
  const persistTask = async (task, status) => {
    if (isLocked) return;

    await setDoc(
      doc(db, "users", user.uid, "days", dayId),
      {
        tasks: {
          [task.id]: {
            title: task.title,
            status,
            updatedAt: serverTimestamp(),
          },
        },
      },
      { merge: true }
    );
  };

  const markComplete = async () => {
    if (!roadmap || isLocked) return;

    const start = roadmap.startedAt.toDate
      ? roadmap.startedAt.toDate()
      : new Date(roadmap.startedAt);

    const date = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + currentDayIndex
    );

    const key = getLocalDateKey(date);

    setCompletedDays((p) => ({ ...p, [key]: true }));

    await updateDoc(doc(db, "roadmaps", user.uid), {
      [`completedDays.${key}`]: true,
    });
  };

  /* ================= HANDLERS ================= */
  const onDragEnd = async (res) => {
    if (!res.destination || isInteractionLocked) return;

    const status = res.destination.droppableId;
    const task = tasks.find((t) => t.id === res.draggableId);
    if (!task) return;

    setTasks((p) => p.map((t) => (t.id === task.id ? { ...t, status } : t)));

    await persistTask(task, status);
  };

  const updateStatus = async (id, status) => {
    if (isInteractionLocked) return;

    const task = tasks.find((t) => t.id === id);
    if (!task) return;

    setTasks((p) => p.map((t) => (t.id === id ? { ...t, status } : t)));

    await persistTask(task, status);
  };

  /* ================= RENDER ================= */

  if (loading) {
    return (
      <Loader>
        <Header>
          <HeaderLeft>
            <SkeletonLine style={{ width: 84, height: 16, borderRadius: 999 }} />
            <SkeletonLine
              style={{ width: 152, height: 30, marginTop: 18, borderRadius: 999 }}
            />
            <SkeletonLine
              style={{ width: "min(520px, 100%)", height: 52, marginTop: 18 }}
            />
            <HeaderMeta>
              <SkeletonLine style={{ width: 138, height: 16 }} />
              <SkeletonLine style={{ width: 220, height: 16 }} />
            </HeaderMeta>
          </HeaderLeft>

          <HeaderRight>
            <SkeletonLine style={{ width: 120, height: 42, borderRadius: 999 }} />
            <SkeletonLine style={{ width: 170, height: 42, borderRadius: 999 }} />
            <SkeletonLine style={{ width: 160, height: 42, borderRadius: 999 }} />
          </HeaderRight>
        </Header>

        <SummaryGrid>
          {Array.from({ length: 4 }).map((_, index) => (
            <SummaryCard key={index}>
              <SkeletonIcon />
              <SummaryContent>
                <SkeletonLine style={{ width: 86, height: 11 }} />
                <SkeletonLine style={{ width: 142, height: 22, marginTop: 10 }} />
                <SkeletonLine style={{ width: "100%", height: 14, marginTop: 12 }} />
                <SkeletonLine style={{ width: "78%", height: 14, marginTop: 8 }} />
              </SummaryContent>
            </SummaryCard>
          ))}
        </SummaryGrid>

        <Board>
          {Array.from({ length: 3 }).map((_, columnIndex) => (
            <Column key={columnIndex}>
              <ColumnHeader status={STATUSES[columnIndex]}>
                <HeaderDot status={STATUSES[columnIndex]} />
                <HeaderText>{STATUSES[columnIndex].replace("_", " ")}</HeaderText>
                <ColumnCount>0</ColumnCount>
              </ColumnHeader>

              {Array.from({ length: 3 }).map((__, cardIndex) => (
                <Card key={cardIndex}>
                  <TaskTitle>
                    <TaskIndex>{cardIndex + 1}</TaskIndex>
                    <SkeletonLine style={{ width: "78%", height: 18 }} />
                  </TaskTitle>
                  <TaskHint>
                    <SkeletonLine style={{ width: "100%", height: 13 }} />
                    <SkeletonLine style={{ width: "88%", height: 13, marginTop: 8 }} />
                  </TaskHint>
                  <SkeletonLine
                    style={{ width: "100%", height: 40, marginTop: 14, borderRadius: 10 }}
                  />
                </Card>
              ))}
            </Column>
          ))}
        </Board>
      </Loader>
    );
  }
  if (!roadmap || !dayId) {
    return (
      <Loader>
        <EmptyState>
          <CheckCircle2 size={18} />
          <div>
            <strong>This task board could not be loaded.</strong>
            <span>Open the day again from your roadmap and try once more.</span>
          </div>
        </EmptyState>
      </Loader>
    );
  }
  function getCountdownVariant({ hours, minutes }) {
    const totalMinutes = hours * 60 + minutes;

    if (totalMinutes <= 0) return "done"; // ✅ day over
    if (totalMinutes <= 60) return "danger"; // 🔴 last 1 hour
    if (totalMinutes <= 250) return "warning"; // 🟠 ~4 hours
    return "safe"; // 🟢 plenty of time
  }

  const dayDate = getDayDateByIndex(roadmap.startedAt, currentDayIndex);
  const dayKey = getLocalDateKey(dayDate);
  const isDayCompleted = !!completedDays[dayKey];
  const isInteractionLocked = isLocked || isDayCompleted || !hasProAccess;
  const doneCount = tasks.filter((task) => task.status === "DONE").length;
  const inProgressCount = tasks.filter(
    (task) => task.status === "IN_PROGRESS"
  ).length;
  const todoCount = tasks.filter((task) => task.status === "TODO").length;
  const completionPercent = tasks.length
    ? Math.round((doneCount / tasks.length) * 100)
    : 0;
  const boardStatusLabel = isDayCompleted
    ? "Completed"
    : isLocked
    ? "Locked until its day arrives"
    : !hasProAccess
    ? "PRO required to manage this board"
    : allTasksCompleted
    ? "Ready to mark complete"
    : "Active and ready to execute";
  return (
    <Container variant={isDayCompleted ? "done" : countdownVariant}>
      <BoardSurface $dimmed={!hasProAccess}>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => navigate(-1)}>
              <ArrowLeft size={16} /> Back
            </BackButton>
            <HeroBadge>
              <Sparkles size={14} /> Daily Task Board
            </HeroBadge>
            <h2>{roadmap?.role ? `${roadmap.role} execution day` : "Focused execution day"}</h2>
            <HeaderMeta>
              <span>
                Day {currentDayIndex + 1}
                {!isLocked && !isDayCompleted && hasProAccess && <StatusPill>ACTIVE</StatusPill>}
                {!hasProAccess && <StatusPill locked>PRO</StatusPill>}
                {isLocked && <StatusPill locked>LOCKED</StatusPill>}
                {isDayCompleted && <StatusPill done>DONE</StatusPill>}
              </span>
              <span>{boardStatusLabel}</span>
            </HeaderMeta>
          </HeaderLeft>

          <HeaderRight>
            {!isLocked && (
              <>
                {!allTasksCompleted ? (
                  <Countdown variant={countdownVariant}>
                    ⏱ {formatRemainingToMidnight(remainingTime)}
                  </Countdown>
                ) : (
                  <Countdown done>🎉 All tasks completed</Countdown>
                )}

                <SecondaryBtn
                  onClick={() => navigate(`/roadmap/day?dayId=${dayId}`)}
                >
                  View Full Day Content
                </SecondaryBtn>

                <PrimaryBtn
                  disabled={isDayCompleted || !allTasksCompleted || !hasProAccess}
                  onClick={markComplete}
                >
                  {isDayCompleted
                    ? "Day Completed"
                    : allTasksCompleted
                    ? "Mark Day Complete"
                    : "Complete all tasks"}
                </PrimaryBtn>
              </>
            )}
          </HeaderRight>
        </Header>

        <SummaryGrid>
          <SummaryCard>
            <SummaryIcon tone="cyan">
              <Layers3 size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Task Progress</SummaryLabel>
              <SummaryValue>{doneCount}/{tasks.length} done</SummaryValue>
              <SummaryHint>
                {completionPercent}% of today&apos;s board is complete.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="amber">
              <Target size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>In Motion</SummaryLabel>
              <SummaryValue>{inProgressCount} in progress</SummaryValue>
              <SummaryHint>
                {todoCount} task{todoCount !== 1 ? "s" : ""} still waiting to be started.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="green">
              <Clock3 size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Today&apos;s Window</SummaryLabel>
              <SummaryValue>
                {isLocked
                  ? "Locked for now"
                  : allTasksCompleted
                  ? "Ready to close"
                  : formatRemainingToMidnight(remainingTime)}
              </SummaryValue>
              <SummaryHint>
                Finish the board in one focused sitting instead of spreading it across random sessions.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon tone="violet">
              <Calendar size={18} />
            </SummaryIcon>
            <SummaryContent>
              <SummaryLabel>Completion State</SummaryLabel>
              <SummaryValue>
                {isDayCompleted ? "Day already completed" : "Complete after all tasks"}
              </SummaryValue>
              <SummaryHint>
                Mark the day complete only after every task reaches the done column.
              </SummaryHint>
            </SummaryContent>
          </SummaryCard>
        </SummaryGrid>

        {isLocked && (
          <Banner locked>
            This task board is locked because this roadmap day is still upcoming. Keep your momentum on the current day first.
          </Banner>
        )}

        {isDayCompleted && (
          <Banner>
            This day has already been marked complete. You can still review the board, but task changes are locked to preserve progress.
          </Banner>
        )}

        {!tasks.length ? (
          <EmptyState>
            <CheckCircle2 size={18} />
            <div>
              <strong>No tasks were generated for this day yet.</strong>
              <span>Open the full day content or come back once tasks are added to this lesson.</span>
            </div>
          </EmptyState>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Board>
              {STATUSES.map((status) => (
                <Droppable droppableId={status} key={status}>
                  {(p) => (
                    <Column ref={p.innerRef} {...p.droppableProps}>
                      <ColumnHeader status={status}>
                        <HeaderDot status={status} />
                        <HeaderText>{status.replace("_", " ")}</HeaderText>
                        <ColumnCount>
                          {tasks.filter((t) => t.status === status).length}
                        </ColumnCount>
                      </ColumnHeader>

                      {tasks
                        .filter((t) => t.status === status)
                        .map((task, i) => (
                          <Draggable key={task.id} draggableId={task.id} index={i}>
                            {(p) => (
                              <Card
                                ref={p.innerRef}
                                {...p.draggableProps}
                                {...p.dragHandleProps}
                              >
                                <TaskTitle>
                                  <TaskIndex>{task.sequence}</TaskIndex>
                                  <strong>{task.title}</strong>
                                </TaskTitle>

                                <TaskHint>
                                  Move this task when you start it, and mark it done only after the output is actually finished.
                                </TaskHint>

                                <StatusSelect
                                  value={task.status}
                                  disabled={isInteractionLocked}
                                  onChange={(e) =>
                                    updateStatus(task.id, e.target.value)
                                  }
                                >
                                  {STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                      {s.replace("_", " ")}
                                    </option>
                                  ))}
                                </StatusSelect>
                              </Card>
                            )}
                          </Draggable>
                        ))}
                      {p.placeholder}
                    </Column>
                  )}
                </Droppable>
              ))}
            </Board>
          </DragDropContext>
        )}
      </BoardSurface>

      {!hasProAccess && (
        <ProOverlay>
          <ProLockCard>
            <ProEyebrow>
              <Sparkles size={14} />
              PRO feature
            </ProEyebrow>
            <ProTitle>Unlock the daily task board with PRO</ProTitle>
            <ProText>
              This board helps you move roadmap work across `To Do`, `In Progress`,
              and `Done` so you can execute each day with clarity instead of guessing.
            </ProText>
            <ProList>
              <li>Drag and organize today&apos;s roadmap tasks</li>
              <li>Track in-progress work and completion in one place</li>
              <li>Finish the day and mark roadmap progress with confidence</li>
            </ProList>
            <ProActions>
              <SecondaryBtn onClick={() => navigate(`/roadmap/day?dayId=${dayId}`)}>
                View Full Day Content
              </SecondaryBtn>
              <PrimaryBtn onClick={() => navigate("/pricing")}>
                Activate PRO
              </PrimaryBtn>
            </ProActions>
          </ProLockCard>
        </ProOverlay>
      )}
    </Container>
  );
}

/* ================= STYLES ================= */
const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 8px 10px;
  margin-bottom: 12px;

  border-radius: 12px;

  background: ${({ status }) => {
    if (status === "TODO") return "rgba(99,102,241,0.12)";
    if (status === "IN_PROGRESS") return "rgba(245,158,11,0.12)";
    if (status === "DONE") return "rgba(34,197,94,0.12)";
    return "rgba(255,255,255,0.05)";
  }};

  border: 1px solid
    ${({ status }) => {
      if (status === "TODO") return "rgba(99,102,241,0.35)";
      if (status === "IN_PROGRESS") return "rgba(245,158,11,0.35)";
      if (status === "DONE") return "rgba(34,197,94,0.35)";
      return "#1f2937";
    }};
`;

const HeaderDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;

  background: ${({ status }) => {
    if (status === "TODO") return "#6366f1";
    if (status === "IN_PROGRESS") return "#f59e0b";
    if (status === "DONE") return "#22c55e";
    return "#9ca3af";
  }};

  box-shadow: 0 0 0 3px
    ${({ status }) => {
      if (status === "TODO") return "rgba(99,102,241,0.25)";
      if (status === "IN_PROGRESS") return "rgba(245,158,11,0.25)";
      if (status === "DONE") return "rgba(34,197,94,0.25)";
      return "transparent";
    }};
`;

const HeaderText = styled.span`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #e5e7eb;
`;

const ColumnCount = styled.span`
  margin-left: auto;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.55);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 800;
`;

const TaskTitle = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const TaskIndex = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 22px;
  height: 22px;

  font-size: 12px;
  font-weight: 700;

  color: #e0e7ff;

  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.9),
    rgba(139, 92, 246, 0.9)
  );

  border-radius: 999px;

  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35),
    inset 0 0 0 1px rgba(255, 255, 255, 0.15);

  flex-shrink: 0;
`;
const Countdown = styled.div`
  font-size: 13px;
  padding: 10px 14px;
  border-radius: 999px;
  font-weight: 800;
  border: 1px solid rgba(148, 163, 184, 0.12);

  animation: ${({ variant }) =>
    variant === "danger" ? "pulse 1.5s infinite" : "none"};

  background: ${({ done, variant }) => {
    if (done) return "#14532d";
    if (variant === "danger") return "#7f1d1d";
    if (variant === "warning") return "#78350f";
    return "#1f2937";
  }};

  color: ${({ done, variant }) => {
    if (done) return "#22c55e";
    if (variant === "danger") return "#fecaca";
    if (variant === "warning") return "#fde68a";
    return "#e5e7eb";
  }};
`;

const Container = styled.div`
  position: relative;
  min-height: 100vh;
  padding: 118px 20px 80px;
  color: #fff;
  width: 100%;
  background: ${({ variant }) => {
    if (variant === "danger")
      return `
        radial-gradient(
          900px at 50% 0%,
          rgba(239, 68, 68, 0.2),
          transparent 60%
        ),
        radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.1), transparent 58%),
        linear-gradient(180deg, #06101d 0%, #040814 100%)
      `;

    if (variant === "warning")
      return `
        radial-gradient(
          900px at 50% 0%,
          rgba(245, 158, 11, 0.2),
          transparent 60%
        ),
        radial-gradient(880px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
        linear-gradient(180deg, #06101d 0%, #040814 100%)
      `;

    if (variant === "done")
      return `
        radial-gradient(
          900px at 50% 0%,
          rgba(34, 197, 94, 0.18),
          transparent 60%
        ),
        radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.1), transparent 58%),
        linear-gradient(180deg, #06101d 0%, #040814 100%)
      `;

    return `
      radial-gradient(
        860px at 12% 0%,
        rgba(245, 158, 11, 0.1),
        transparent 60%
      ),
      radial-gradient(
        900px at 88% 6%,
        rgba(56, 189, 248, 0.12),
        transparent 60%
      ),
      linear-gradient(180deg, #06101d 0%, #040814 100%)
    `;
  }};

  transition: background 0.6s ease;

  @media (max-width: 640px) {
    padding: 96px 14px 56px;
  }
`;

const BoardSurface = styled.div`
  position: relative;
  z-index: 1;
  filter: ${({ $dimmed }) => ($dimmed ? "blur(2px)" : "none")};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.52 : 1)};
  transform: ${({ $dimmed }) => ($dimmed ? "scale(0.995)" : "none")};
  transition: filter 0.25s ease, opacity 0.25s ease, transform 0.25s ease;
`;

const StatusSelect = styled.select`
  width: 100%;
  margin-top: 10px;
  padding: 8px 36px 8px 12px; /* right padding for icon */
  border-radius: 10px;

  background-color: #020617;
  color: #e5e7eb;

  border: 1px solid rgba(99, 102, 241, 0.45);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  /* 🔥 remove native arrow */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  /* 🔥 custom arrow (SVG inline, HIGH contrast) */
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1l5 5 5-5' fill='none' stroke='%23E5E7EB' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 8px;

  &:hover {
    border-color: #6366f1;
  }

  &:focus {
    outline: none;
    border-color: #8b5cf6;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.3);
  }

  option {
    background: #020617;
    color: #e5e7eb;
  }
`;

const Board = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.84),
    rgba(2, 6, 23, 0.8)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 24px;
  padding: 14px;
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.22);
  h4 {
    color: #9ca3af;
    margin-bottom: 10px;
  }
`;
const StatusPill = styled.span`
  margin-left: 10px;
  font-size: 11px;
  padding: 5px 10px;
  border-radius: 999px;
  background: ${({ locked, done }) =>
    done ? "rgba(34, 197, 94, 0.18)" : locked ? "rgba(239, 68, 68, 0.18)" : "rgba(56, 189, 248, 0.16)"};
  color: ${({ locked, done }) =>
    done ? "#86efac" : locked ? "#fca5a5" : "#7dd3fc"};
  border: 1px solid
    ${({ locked, done }) =>
      done
        ? "rgba(34, 197, 94, 0.26)"
        : locked
        ? "rgba(239, 68, 68, 0.26)"
        : "rgba(56, 189, 248, 0.24)"};
`;

const Card = styled.div`
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.9),
    rgba(2, 6, 23, 0.9)
  );
  border: 1px solid rgba(56, 189, 248, 0.16);
  border-radius: 18px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);

  select {
    width: 100%;
    margin-top: 8px;
    background: #020617;
    color: #fff;
    border-radius: 8px;
  }
`;

const ProOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(
    180deg,
    rgba(4, 8, 20, 0.26),
    rgba(4, 8, 20, 0.52)
  );
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 148px 20px 40px;

  @media (max-width: 640px) {
    padding: 120px 14px 32px;
  }
`;

const ProLockCard = styled.div`
  width: min(100%, 620px);
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.86),
    rgba(2, 6, 23, 0.9)
  );
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.32);
  text-align: center;
`;

const ProEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.24);
  color: #fbbf24;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const ProTitle = styled.h3`
  margin: 18px 0 10px;
  font-size: clamp(28px, 4vw, 38px);
  line-height: 1.08;
  color: #f8fafc;
`;

const ProText = styled.p`
  margin: 0 auto;
  max-width: 520px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const ProList = styled.ul`
  margin: 18px auto 0;
  max-width: 430px;
  padding-left: 20px;
  text-align: left;
  color: #cbd5e1;
  line-height: 1.8;

  li + li {
    margin-top: 8px;
  }
`;

const ProActions = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Loader = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.1), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
  color: #cbd5e1;

  @media (max-width: 640px) {
    padding: 96px 14px 56px;
  }
`;

const SkeletonLine = styled.div`
  background: linear-gradient(
    90deg,
    rgba(30, 41, 59, 0.86) 0%,
    rgba(51, 65, 85, 0.96) 50%,
    rgba(30, 41, 59, 0.86) 100%
  );
  background-size: 200% 100%;
  border-radius: 12px;
  animation: shimmer 1.5s linear infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: linear-gradient(
    90deg,
    rgba(30, 41, 59, 0.86) 0%,
    rgba(51, 65, 85, 0.96) 50%,
    rgba(30, 41, 59, 0.86) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  max-width: 1240px;
  margin: 0 auto 28px;

  @media (max-width: 980px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  max-width: 760px;

  h2 {
    margin-top: 14px;
    font-size: clamp(30px, 4vw, 44px);
    line-height: 1.04;
    font-weight: 1000;
    color: #f8fafc;
  }

  span {
    color: #9ca3af;
    font-size: 14px;
  }
`;

const HeaderMeta = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  span {
    color: #94a3b8;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 980px) {
    justify-content: flex-start;
  }
`;

const PrimaryBtn = styled.button`
  padding: 12px 16px;
  border-radius: 999px;
  border: none;
  font-weight: 800;
  cursor: pointer;
  background: ${({ disabled }) =>
    disabled
      ? "rgba(30, 41, 59, 0.85)"
      : "linear-gradient(135deg, #f59e0b, #f97316)"};
  color: ${({ disabled }) => (disabled ? "#64748b" : "#020617")};
`;

const SecondaryBtn = styled.button`
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid rgba(56, 189, 248, 0.22);
  background: rgba(15, 23, 42, 0.74);
  color: #dbeafe;
  cursor: pointer;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #94a3b8;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    color: #f8fafc;
  }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
`;

const SummaryGrid = styled.div`
  max-width: 1240px;
  margin: 0 auto 28px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  display: flex;
  gap: 14px;
  padding: 18px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.82),
    rgba(2, 6, 23, 0.78)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.22);
`;

const SummaryIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: ${({ tone }) =>
    tone === "amber"
      ? "rgba(245, 158, 11, 0.16)"
      : tone === "green"
      ? "rgba(34, 197, 94, 0.16)"
      : tone === "violet"
      ? "rgba(99, 102, 241, 0.18)"
      : "rgba(56, 189, 248, 0.16)"};
  color: ${({ tone }) =>
    tone === "amber"
      ? "#fbbf24"
      : tone === "green"
      ? "#86efac"
      : tone === "violet"
      ? "#c4b5fd"
      : "#7dd3fc"};
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const SummaryContent = styled.div``;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7dd3fc;
`;

const SummaryValue = styled.div`
  margin-top: 8px;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 900;
  color: #f8fafc;
`;

const SummaryHint = styled.p`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #94a3b8;
`;

const Banner = styled.div`
  max-width: 1240px;
  margin: 0 auto 24px;
  padding: 16px 18px;
  border-radius: 18px;
  background: ${({ locked }) =>
    locked
      ? "rgba(127, 29, 29, 0.18)"
      : "rgba(34, 197, 94, 0.12)"};
  border: 1px solid
    ${({ locked }) =>
      locked
        ? "rgba(239, 68, 68, 0.24)"
        : "rgba(34, 197, 94, 0.2)"};
  color: ${({ locked }) => (locked ? "#fecaca" : "#bbf7d0")};
  font-size: 14px;
  line-height: 1.6;
`;

const EmptyState = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.82),
    rgba(2, 6, 23, 0.78)
  );
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #cbd5e1;

  strong {
    display: block;
    color: #f8fafc;
    font-size: 16px;
  }

  span {
    display: block;
    margin-top: 6px;
    color: #94a3b8;
    line-height: 1.6;
  }
`;

const TaskHint = styled.p`
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #94a3b8;
`;
